import React from 'react';

import {controlStyle, disabledControlStyle} from '../lib/formStyles.js';

// ============================================================================
// NumberInput — numbers (Design Constitution I04, Q36)
// ----------------------------------------------------------------------------
// The decimal keypad on phones (inputMode), no spinners (index.html), and the
// whole value selected on focus (index.html focusin handler) so typing replaces
// it. No '0' placeholder: an empty field stays empty (Q36). The value is the
// raw string the caller stores today.
// stepper: a [−] value [+] row with 44px buttons, only for small counts and
// date nudges (I04).
// ============================================================================

type NumberInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> & {
  value: string;
  onChange: (next: string) => void;
  integer?: boolean;
  stepper?: boolean;
};

const stepButton: React.CSSProperties = {
  width: 44,
  height: 44,
  flexShrink: 0,
  border: '1px solid var(--border-strong)',
  borderRadius: 12,
  background: '#fff',
  color: 'var(--ink)',
  fontFamily: 'inherit',
  fontSize: 18,
  fontWeight: 700,
  lineHeight: 1,
  cursor: 'pointer',
};

// One stepper press: move to the next value on the input's own step grid
// (counted from min, as the browser does), then clamp into [min, max] on that
// grid. Returns null when the press cannot move the value that way.
export function nudge(value: string, direction: 1 | -1, step: number, min?: number, max?: number): string | null {
  const current = Number(value);
  const base = min ?? 0;
  const from = value.trim() !== '' && Number.isFinite(current) ? current : base;
  const k = (from - base) / step;
  const nextK = direction > 0 ? Math.floor(k + 1e-9) + 1 : Math.ceil(k - 1e-9) - 1;
  let next = base + nextK * step;
  if (max != null && next > max) next = base + Math.floor((max - base) / step + 1e-9) * step;
  if (min != null && next < min) next = min;
  next = Number(next.toFixed(10));
  if (value.trim() !== '' && (direction > 0 ? next <= current : next >= current)) return null;
  return String(next);
}

export default function NumberInput({
  value,
  onChange,
  integer = false,
  stepper = false,
  style,
  min,
  max,
  ...rest
}: NumberInputProps) {
  const input = (
    <input
      {...rest}
      type="number"
      inputMode={integer ? 'numeric' : 'decimal'}
      step={rest.step ?? (integer ? 1 : 'any')}
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-number-input="1"
      style={{...controlStyle, ...(stepper ? {textAlign: 'center', flex: 1, minWidth: 0} : null), ...style}}
    />
  );
  if (!stepper) return input;
  const lo = min == null || min === '' ? undefined : Number(min);
  const hi = max == null || max === '' ? undefined : Number(max);
  const stepNum = Number(rest.step);
  const step = Number.isFinite(stepNum) && stepNum > 0 ? stepNum : 1;
  // The buttons never write what the input itself would refuse: nothing while
  // the field is disabled or read-only, and nothing past min/max.
  const locked = Boolean(rest.disabled) || Boolean(rest.readOnly);
  const down = locked ? null : nudge(value, -1, step, lo, hi);
  const up = locked ? null : nudge(value, 1, step, lo, hi);
  const button = (label: string, sign: string, next: string | null) => (
    <button
      type="button"
      className="wcf-clickable"
      aria-label={label}
      disabled={next == null}
      style={{...stepButton, ...(next == null ? disabledControlStyle : null)}}
      onClick={() => {
        if (next != null) onChange(next);
      }}
    >
      {sign}
    </button>
  );
  return (
    <div data-number-stepper="1" style={{display: 'flex', gap: 8, alignItems: 'center'}}>
      {button('Decrease', '−', down)}
      {input}
      {button('Increase', '+', up)}
    </div>
  );
}
